import { NavigateFunction, useNavigate, useRouteError } from 'react-router-dom';
import { Button, Result, Typography } from 'antd';
import { BugOutlined, CloseCircleOutlined } from '@ant-design/icons';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isNullOrWhiteSpaces } from './shared/utils/strings';
import { useClosestMedia } from './shared/hooks/media-queries';
import { useAuth } from 'react-oidc-context';
import { bugReportEvents } from 'src/common/bug-report/bug-report-context';
// import { useHubSpotLiveChat } from 'src/shared/components/hubspot-live-chat';
const { Paragraph, Text } = Typography;

export interface ErrorPageProps {
  errorMessage?: string;
  redirectTo?: { label: string; url?: string; onClick?: () => void };
  title?: string;
  subtitle?: string;
}

function buildErrorContext(error: any, errorMessage?: string): string {
  const parts: string[] = [`URL: ${window.location.href}`];
  if (error?.status) parts.push(`Estado: ${error.status}`);
  if (error?.statusText) parts.push(`Texto de estado: ${error.statusText}`);
  if (error?.message) parts.push(`Mensaje: ${error.message}`);
  if (errorMessage) parts.push(`Detalle: ${errorMessage}`);
  return parts.join('\n');
}

const ErrorPage: React.FC<ErrorPageProps> = ({ title = undefined, subtitle = undefined, errorMessage = undefined, redirectTo = undefined }) => {
  const closestMedia = useClosestMedia();
  const isSmallView = ['xs', 'sm', 'md'].includes(closestMedia as string);

  const error = useRouteError() as any;
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    bugReportEvents.pulse();
  }, []);

  const renderErrorMessages = (messages: (string | undefined)[]) => (
    <div className="desc">
      <Paragraph>
        <Text
          strong
          style={{
            fontSize: 16
          }}
        >
          {t('Error message')}:
        </Text>
      </Paragraph>
      {messages
        .filter((x) => x)
        .map((txt, idx) => (
          <Paragraph key={idx}>
            <CloseCircleOutlined style={{ color: 'red' }} /> {t('{{txt}}', { txt })}
          </Paragraph>
        ))}
    </div>
  );

  const renderContent = () => {
    const messages = [error?.statusText, error?.message, error?.data, errorMessage];

    if (window.REACT_APP_ENV !== 'prod') {
      if (!isNullOrWhiteSpaces(errorMessage) || error?.statusText || error?.message) {
        return renderErrorMessages(messages);
      }
    } else {
      if (!isNullOrWhiteSpaces(errorMessage)) {
        return renderErrorMessages([errorMessage]);
      }
    }

    return null;
  };

  // const {
  //   isReady: isHubSpotLiveChatReady,
  //   refreshWidget: refreshHubSpotLiveChatWidget,
  //   openWidget: openHubSpotLiveChatWidget
  // } = useHubSpotLiveChat();

  // useEffect(() => {
  //   if (isHubSpotLiveChatReady) {
  //     openHubSpotLiveChatWidget();
  //     refreshHubSpotLiveChatWidget(true);
  //   }
  // }, [isHubSpotLiveChatReady, refreshHubSpotLiveChatWidget, openHubSpotLiveChatWidget]);

  const getQueryParams = (param: string) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(param);
  };

  const notification = getQueryParams('fromNotification');

  if (notification) {
    errorMessage = t('El enlace que has seguido desde la notificación ha caducado.');
  }

  if (error != null) {
    console.error(error);

    const status = error.response?.status;
    const rejectionReason = error.response?.headers?.['x-auth-rejection-reason'];

    const isUnauthorized = status === 401;

    if (isUnauthorized && rejectionReason === 'account-deleted') {
      return (
        <UnauthorizedErrorPage
          title={t('Tu cuenta ha sido eliminada')}
          subtitle={t('Parece que esta cuenta ya no está disponible. Por favor, contacta con soporte si crees que esto es un error.')}
        />
      );
    }

    if (isUnauthorized) {
      return <UnauthorizedErrorPage />;
    }
  }

  return (
    <div
      style={{
        padding: isSmallView ? '1%' : '1% 20%',
        minHeight: '100vh'
      }}
    >
      <Result
        style={{
          backgroundColor: 'white',
          borderRadius: 8
        }}
        status="500"
        title={title ?? t('Oops')}
        subTitle={subtitle ?? t('Sorry, something went wrong.')}
        extra={[
          <Button key="home" size="large" onClick={redirectTo?.onClick ?? (() => navigate(redirectTo?.url ?? '/'))} type="primary">
            {redirectTo?.label ?? t('Back Home')}
          </Button>,
          <Button
            key="report"
            size="large"
            icon={<BugOutlined />}
            onClick={() => bugReportEvents.openModal(buildErrorContext(error, errorMessage), 'error-page')}
          >
            {t('Reportar este error')}
          </Button>
        ]}
      >
        {renderContent()}
      </Result>
    </div>
  );
};

export const NotFound: React.FC<ErrorPageProps> = ({ title = undefined, subtitle = undefined, redirectTo = undefined }) => {
  const { t } = useTranslation();
  let navigate: NavigateFunction;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate();
  } catch {
    navigate = (s: any) => {
      window.location.href = window.REACT_APP_OIDC_REDIRECT_URI;
    };
  }
  return (
    <div
      style={{
        padding: '1% 20%'
      }}
    >
      <Result
        status="404"
        title={title ?? t('No encontrado')}
        subTitle={subtitle ?? t('Lo sentimos, el servicio solicitado no existe')}
        extra={[
          <Button key="home" size="large" onClick={redirectTo?.onClick ?? (() => navigate(redirectTo?.url ?? '/'))} type="primary">
            {redirectTo?.label ?? t('A inicio')}
          </Button>,
          <Button
            key="report"
            size="large"
            icon={<BugOutlined />}
            onClick={() => bugReportEvents.openModal(`URL: ${window.location.href}`, 'error-page')}
          >
            {t('Reportar este error')}
          </Button>
        ]}
      />
    </div>
  );
};

export const UnauthorizedErrorPage: React.FC<ErrorPageProps> = ({ title = undefined, subtitle = undefined, redirectTo = undefined }) => {
  const closestMedia = useClosestMedia();
  const isSmallView = ['xs', 'sm', 'md'].includes(closestMedia as string);

  const { t } = useTranslation();
  const auth = useAuth();

  if (redirectTo == null) redirectTo = auth?.isAuthenticated ? { label: t('Salir'), onClick: () => auth.signoutRedirect() } : undefined;

  return (
    <div
      style={{
        padding: isSmallView ? '1%' : '1% 20%',
        minHeight: '100vh'
      }}
    >
      <Result
        style={{
          backgroundColor: 'white',
          borderRadius: 8
        }}
        status="403"
        title={title ?? t('No autorizado')}
        subTitle={subtitle ?? t('Parece que no tienes una sesión válida o tu cuenta no está autorizada.')}
        extra={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {redirectTo && (
              <Button size="large" onClick={redirectTo.onClick} type="primary">
                {redirectTo.label}
              </Button>
            )}
            <Button size="large" icon={<BugOutlined />} onClick={() => bugReportEvents.openModal(`URL: ${window.location.href}`, 'error-page')}>
              {t('Reportar este error')}
            </Button>
          </div>
        }
      />
    </div>
  );
};

export const Page404 = () => {
  const { t } = useTranslation();
  return <ErrorPage errorMessage={t('Página no encontrada')} redirectTo={{ label: t('Back Home'), url: '/default-user-page' }} />;
};

export const WrongNotificationErrorPage = () => {
  const { t } = useTranslation();
  return (
    <ErrorPage
      errorMessage={t('Parece que esta notificación no es para ti. Por favor, asegúrate de estar usando la cuenta correcta.')}
      redirectTo={{ label: t('Back Home'), url: '/default-user-page' }}
    />
  );
};

export const WrongUserErrorPage = () => {
  const { t } = useTranslation();
  return (
    <ErrorPage
      errorMessage={t('Parece que este enlace no es para ti. Por favor, asegúrate de estar usando la cuenta correcta.')}
      redirectTo={{ label: t('Volver a Inicio'), url: '/default-user-page' }}
    />
  );
};

export default ErrorPage;
